'use client';
/**
 * Global Alerts Drawer — slide-in from the right. Opened by the AppNav
 * ACTIONS chip, the ALERTS toggle "OPEN" button, or any SignalDot click
 * (the latter sets symbolFilter so the drawer shows only that symbol).
 *
 * Categorized by severity (HIGH / MEDIUM / LOW), with the cleared/
 * handled set collapsed at the bottom. Each card carries the headline
 * (PM-facing price language), the detail row (model interpretation),
 * the suggested action verb, and per-alert action buttons.
 */

import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAlerts } from './AlertsContext';
import { AlertEvidence } from './alerts-drawer/AlertEvidence';
import { useResource } from '@/lib/use-resource';
import type { ActionAlert, ActionAlertsResponse, AlertSeverity } from '@/lib/action-types';

const SEVERITY_RANK: Record<AlertSeverity, number> = { high: 3, medium: 2, low: 1 };

const SEVERITY_TONE: Record<AlertSeverity, { border: string; chip: string; chipText: string; label: string }> = {
  high:   { border: 'border-l-red-500',    chip: 'bg-red-500/20',    chipText: 'text-red-300',    label: 'HIGH' },
  medium: { border: 'border-l-amber-500',  chip: 'bg-amber-500/20',  chipText: 'text-amber-300',  label: 'MEDIUM' },
  low:    { border: 'border-l-slate-400',  chip: 'bg-slate-500/15',  chipText: 'text-slate-300',  label: 'LOW' },
};

function fmtRelTime(ts: string): string {
  const ms = Date.now() - Date.parse(ts);
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

/** Underlying-event date/time formatter — Steve directive 2026-05-23:
 *  show the day of week alongside the date so the temporal context is
 *  immediate ("Mon May 18 · 16:12"). Falls back to relative-time when
 *  the event happened today. */
function fmtEventDate(ts: string): string {
  const d = new Date(ts);
  if (!Number.isFinite(d.getTime())) return ts;
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return fmtRelTime(ts);
  const dow = d.toLocaleString('en-US', { weekday: 'short' });
  const mo = d.toLocaleString('en-US', { month: 'short' });
  const day = d.getDate();
  const hh = d.getHours().toString().padStart(2, '0');
  const mm = d.getMinutes().toString().padStart(2, '0');
  return `${dow} ${mo} ${day} · ${hh}:${mm}`;
}

interface ScopeFilter { id: 'all' | 'held' | 'alpha-zone'; label: string }
const SCOPE_FILTERS: ScopeFilter[] = [
  { id: 'all', label: 'All' },
  { id: 'held', label: 'Held' },
  { id: 'alpha-zone', label: 'Alpha Zone' },
];

export function AlertsDrawer() {
  const { drawerOpen, closeDrawer, symbolFilter, clearSymbolFilter, alertsEnabled } = useAlerts();
  const { data, refetch } = useResource<ActionAlertsResponse>('/api/action-alerts', 60_000);
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter['id']>('all');

  // Close on Esc.
  useEffect(() => {
    if (!drawerOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') closeDrawer(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [drawerOpen, closeDrawer]);

  const active = useMemo(() => {
    let xs = data?.active ?? [];
    if (symbolFilter) xs = xs.filter(a => a.symbol === symbolFilter);
    if (scopeFilter === 'held') xs = xs.filter(a => a.inHeld ?? a.scope === 'held');
    else if (scopeFilter === 'alpha-zone') xs = xs.filter(a => a.inAlphaZone ?? a.scope === 'alpha-zone');
    // Sort by severity first, then by underlying-event time newest-first.
    // Falls back to monitor tick time when the alert has no eventTs (price
    // moves don't carry a single point-in-time event). Newest-first
    // ordering within each severity band.
    return xs.sort((a, b) => {
      const sev = SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity];
      if (sev !== 0) return sev;
      const at = Date.parse(a.eventTs ?? a.ts);
      const bt = Date.parse(b.eventTs ?? b.ts);
      return (Number.isFinite(bt) ? bt : 0) - (Number.isFinite(at) ? at : 0);
    });
  }, [data, symbolFilter, scopeFilter]);

  const cleared = data?.cleared ?? [];

  const grouped = useMemo(() => {
    const out: Record<AlertSeverity, ActionAlert[]> = { high: [], medium: [], low: [] };
    for (const a of active) out[a.severity].push(a);
    return out;
  }, [active]);

  if (!drawerOpen) return null;
  // SSR guard — portal target only exists in the browser
  if (typeof document === 'undefined') return null;

  // Portal to body so the drawer escapes any transformed/stacking-context
  // parent (the 3D scatter labels were punching through z-60 because the
  // R3F Html canvas creates its own stacking context). Then a very high
  // z-index ensures we sit above everything else on the page.
  return createPortal(
    <div
      className="fixed inset-0 z-[2147483000] bg-black/90 backdrop-blur-xl"
      onClick={closeDrawer}
      role="dialog"
      aria-modal="true"
      aria-label="Alerts drawer"
    >
      <aside
        className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-[#0a0e14] border-l border-[#1e293b] shadow-2xl overflow-y-auto flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="sticky top-0 bg-[#0a0e14] border-b border-[#1e293b] px-5 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-[13px] font-bold text-white tracking-wide uppercase">Alerts</h2>
            <p className="text-[11px] text-[#64748b] mt-0.5">
              {alertsEnabled
                ? `${active.length} active · ${cleared.length} cleared today`
                : 'Alerts are OFF — toggle the pill on the visualizer page to re-enable'}
            </p>
          </div>
          <button
            type="button"
            onClick={closeDrawer}
            className="text-[#64748b] hover:text-white text-xl leading-none px-2"
            aria-label="Close drawer"
          >
            ×
          </button>
        </header>

        {/* Symbol filter chip + scope filter */}
        <div className="px-5 py-3 border-b border-[#1e293b] flex flex-wrap items-center gap-2">
          {symbolFilter && (
            <button
              type="button"
              onClick={clearSymbolFilter}
              className="text-[10px] uppercase tracking-wider text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-md px-2 py-1 hover:bg-amber-500/20"
              title="Clear symbol filter"
            >
              {symbolFilter} ✕ show all
            </button>
          )}
          <div className="flex gap-1">
            {SCOPE_FILTERS.map(f => (
              <button
                key={f.id}
                type="button"
                onClick={() => setScopeFilter(f.id)}
                className={`text-[10px] tracking-wide uppercase rounded-md px-2 py-1 border transition-colors ${
                  scopeFilter === f.id
                    ? 'bg-[#1e293b] text-white border-[#334155]'
                    : 'text-[#64748b] border-[#1e293b] hover:text-white hover:border-[#334155]'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Active alerts */}
        <div className="flex-1 px-5 py-4 space-y-5">
          {(['high', 'medium', 'low'] as AlertSeverity[]).map(sev =>
            grouped[sev].length > 0 ? (
              <SeveritySection
                key={sev}
                severity={sev}
                alerts={grouped[sev]}
                onAction={() => refetch()}
                onNavigate={closeDrawer}
              />
            ) : null,
          )}
          {active.length === 0 && (
            <div className="text-[12px] text-[#64748b] italic py-8 text-center">
              {alertsEnabled
                ? 'No active alerts. The desk is watching; you can rest.'
                : 'Alerts are OFF. Re-enable from the toggle on the visualizer page to see what fires next.'}
            </div>
          )}
          {cleared.length > 0 && (
            <ClearedSection alerts={cleared} />
          )}
        </div>
      </aside>
    </div>,
    document.body,
  );
}

function SeveritySection({ severity, alerts, onAction, onNavigate }: { severity: AlertSeverity; alerts: ActionAlert[]; onAction: () => void; onNavigate: () => void }) {
  const tone = SEVERITY_TONE[severity];
  return (
    <section>
      <h3 className={`text-[10px] font-bold tracking-widest uppercase mb-2 ${tone.chipText}`}>
        {tone.label} ({alerts.length})
      </h3>
      <div className="space-y-2">
        {alerts.map(a => <AlertCard key={a.id} alert={a} onAction={onAction} onNavigate={onNavigate} />)}
      </div>
    </section>
  );
}

function ClearedSection({ alerts }: { alerts: ActionAlert[] }) {
  const [expanded, setExpanded] = useState(false);
  if (alerts.length === 0) return null;
  return (
    <section className="pt-4 border-t border-[#1e293b]/60">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="text-[10px] uppercase tracking-widest text-[#64748b] hover:text-white"
      >
        {expanded ? '▾' : '▸'} Cleared / handled today ({alerts.length})
      </button>
      {expanded && (
        <div className="space-y-2 mt-2 opacity-70">
          {alerts.map(a => <AlertCard key={a.id} alert={a} muted />)}
        </div>
      )}
    </section>
  );
}

/** Formatter helpers for the trigger detail grid. Lifted out of the
 *  component so renderHeadline-style key-aware formatting stays in one
 *  place. Steve directive 2026-05-23: show the data that fired the
 *  alert, not a stock summary. */
function AlertCard({ alert, onAction, onNavigate: _onNavigate, muted = false }: { alert: ActionAlert; onAction?: () => void; onNavigate?: () => void; muted?: boolean }) {
  const tone = SEVERITY_TONE[alert.severity];
  const [busy, setBusy] = useState(false);

  const act = async (action: 'dismiss' | 'handle') => {
    if (busy || !onAction) return;
    setBusy(true);
    try {
      await fetch('/api/action-alerts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, alertId: alert.id, rule: alert.rule, symbol: alert.symbol }),
      });
      onAction();
    } finally { setBusy(false); }
  };

  return (
    <div
      className={`border-l-2 ${tone.border} bg-[#0d1117] border border-[#1e293b] rounded-md p-3 ${muted ? '' : 'hover:border-[#334155]'}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-mono font-bold text-[13px] text-white">{alert.symbol}</span>
          <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${tone.chip} ${tone.chipText}`}>{tone.label}</span>
          {alert.inHeld && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">held</span>}
          {alert.inAlphaZone && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-300 border border-blue-500/20">α-zone</span>}
        </div>
        <span className="text-[9px] text-[#64748b] font-mono shrink-0 text-right">
          {alert.eventTs ? (
            <span title={`Underlying event: ${alert.eventTs}`}>{fmtEventDate(alert.eventTs)}</span>
          ) : (
            fmtRelTime(alert.ts)
          )}
        </span>
      </div>
      <div className="text-[12px] text-[#e2e8f0] mb-1.5 leading-snug">
        {alert.detail.sourceUrl ? (
          <a
            href={String(alert.detail.sourceUrl)}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-white underline decoration-[#475569] hover:decoration-white transition-colors"
            onClick={e => e.stopPropagation()}
          >
            {alert.headline}
          </a>
        ) : alert.headline}
      </div>
      <AlertEvidence alert={alert} />
      {alert.suggestedAction && (
        <div className="mt-1 text-[10px] uppercase tracking-wider font-semibold text-[#64748b]">
          {alert.suggestedAction}
        </div>
      )}
      {!muted && onAction && (
        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-[#1e293b]/60">
          <button
            type="button"
            onClick={() => act('handle')}
            disabled={busy}
            className="text-[10px] uppercase tracking-wider text-emerald-300 hover:text-emerald-200 border border-emerald-500/30 hover:border-emerald-500/60 rounded px-2 py-0.5 disabled:opacity-50"
            title="Done — I acted on this. Moves to archive."
          >
            done
          </button>
          <button
            type="button"
            onClick={() => act('dismiss')}
            disabled={busy || !!alert.dismissedAt}
            className="text-[10px] uppercase tracking-wider text-[#94a3b8] hover:text-white border border-[#1e293b] hover:border-[#334155] rounded px-2 py-0.5 disabled:opacity-50"
            title="Seen — I've reviewed this. Alert stays but greys out."
          >
            {alert.dismissedAt ? 'seen ✓' : 'seen'}
          </button>
        </div>
      )}
    </div>
  );
}

/** One compact line of hard evidence — the exact numbers that triggered the alert.
 *  No jargon, no boxes, no rule definitions. Just the facts. */
